import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../../../../components/ui/dialog';
import { Button } from '../../../../../components/ui/button';
import { Label } from '../../../../../components/ui/label';
import { MultiSelect } from '../../../../../components/ui/multi-select';
import { FileUpload } from '../../../../../components/ui/FileUpload';
import { Upload } from 'lucide-react';
import { MaterialData } from '../../../types/materials';
import { DocumentTag } from '../../../types';
import { getPreSignedUrlToUpload, updateMaterialTraceability } from '../../../../../redux/Project/materialSaga';
import customNotification from '../../../../../components/ui/custom-notification';
import AddNewItemDialog from '../../../../../components/ui/AddNewItemDialog';
import { AddMaterialCategory } from '../../../../../redux/Project/materialSaga';

interface UploadBatchMaterialDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUploadComplete: () => void;
  materials: MaterialData[];
  selectedPipeSize: any;
  documentTags: DocumentTag[];
  setDocumentTags: (tags: DocumentTag[]) => void;
}

const isPipeTabCategory = (c: string | undefined) =>
  c === 'Pipe' || c === 'Bends' || c === 'Flanges';

export const UploadBatchMaterialDocumentModal: React.FC<UploadBatchMaterialDocumentModalProps> = ({
  isOpen,
  onClose,
  onUploadComplete,
  materials,
  selectedPipeSize,
  documentTags,
  setDocumentTags
}) => {
  const [selectedHeatNumbers, setSelectedHeatNumbers] = useState<string[]>([]);
  const [materialDocuments, setMaterialDocuments] = useState<File[]>([]);
  const [selectedTags, setSelectedTags] = useState<any[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [isAddingTag, setIsAddingTag] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Local state for documentTags to ensure immediate updates
  const [localDocumentTags, setLocalDocumentTags] = useState<DocumentTag[]>(documentTags);

  // Get available heat numbers from both Pipe and Forge
  const availableHeatNumbers = useMemo(() => {
    const heatNumbers = new Set<string>();
    materials.forEach((material: MaterialData) => {
      if (isPipeTabCategory(material.materialCategory) && material.details?.pipeHeatNumber) {
        heatNumbers.add(material.details.pipeHeatNumber);
      } else if (material.materialCategory === 'Forge' && material.details?.forgeHeatNumber) {
        heatNumbers.add(material.details.forgeHeatNumber);
      }
    });
    return Array.from(heatNumbers).sort();
  }, [materials]);

  // Convert heat numbers to options for MultiSelect
  const heatNumberOptions = useMemo(() => 
    availableHeatNumbers.map(heatNumber => ({
      value: heatNumber,
      label: heatNumber
    })), [availableHeatNumbers]
  );

  // Get tags from both Pipe and Forge
  const getTags = () => {
    const pipeTags = localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_PIPE_TAGS');
    const forgeTags = localDocumentTags.filter((tag: any) => tag.tags_type === 'MATERIAL_FORGE_TAGS');
    return [...pipeTags, ...forgeTags];
  };

  // Convert document tags to options for the MultiSelect component
  const tagOptions = useMemo(() =>
    getTags().map(tag => ({
      value: tag.tags_id.toString(),
      label: tag.tags_name
    })), [localDocumentTags]
  );

  // Sync local state with parent state when documentTags change
  useEffect(() => {
    setLocalDocumentTags(documentTags);
  }, [documentTags]);

  // Initialize local state when dialog opens
  useEffect(() => {
    if (isOpen) {
      setLocalDocumentTags(documentTags);
      setSelectedHeatNumbers([]);
      setMaterialDocuments([]);
      setSelectedTags([]);
    }
  }, [isOpen, documentTags]);

  const handleMaterialFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const fileArray = Array.from(files);
      setMaterialDocuments(prev => [...prev, ...fileArray]);
      // Initialize tags array for new files
      const newTags = Array(fileArray.length).fill([]);
      setSelectedTags(prev => [...prev, ...newTags]);
    }
  };

  const handleRemoveFile = (index: number) => {
    setMaterialDocuments(prev => prev.filter((_, i) => i !== index));
    setSelectedTags(prev => prev.filter((_, i) => i !== index));
  };

  const handleTagsChange = (fileIndex: number, tags: string[]) => {
    const updatedTags = [...selectedTags];
    updatedTags[fileIndex] = tags;
    setSelectedTags(updatedTags);
  };

  const handleAddTag = async (tagName: string) => {
    if (tagName.trim()) {
      if (getTags().find((tag: any) => tag.tags_name === tagName.trim())) {
        customNotification.warning(`${tagName.trim()} tag already exists`);
        return;
      }

      // Default to Pipe tags, but user can add tags for either category
      const apiPayload = {
        tags_name: tagName.trim(),
        project_id: Number(selectedPipeSize.projectId),
        tags_type: 'MATERIAL_PIPE_TAGS', // Default to Pipe, can be changed if needed
        created_by: user.email || ''
      };
      const res = await AddMaterialCategory(apiPayload);

      if (res) {
        const updatedLocalTags = [...documentTags, res];
        setLocalDocumentTags(updatedLocalTags);
        setDocumentTags(updatedLocalTags);
        setIsAddingTag(false);
      }
    }
  };

  const handleUpload = async () => {
    if (!selectedPipeSize) {
      customNotification.error('Error', 'Please select a pipe size');
      return;
    }

    if (selectedHeatNumbers.length === 0) {
      customNotification.warning('Please select at least one heat number');
      return;
    }

    if (materialDocuments.length === 0) {
      customNotification.warning('Please upload at least one document');
      return;
    }

    setIsUploading(true);
    try {
      // Get materials matching selected heat numbers from both Pipe and Forge
      const matchingMaterials = materials.filter((material: MaterialData) => {
        if (isPipeTabCategory(material.materialCategory)) {
          return material.details?.pipeHeatNumber && selectedHeatNumbers.includes(material.details.pipeHeatNumber);
        } else if (material.materialCategory === 'Forge') {
          return material.details?.forgeHeatNumber && selectedHeatNumbers.includes(material.details.forgeHeatNumber);
        }
        return false;
      });

      if (matchingMaterials.length === 0) {
        customNotification.warning('No materials found for selected heat numbers');
        setIsUploading(false);
        return;
      }

      // Group materials by category for API calls
      const pipeMaterials = matchingMaterials.filter(m => isPipeTabCategory(m.materialCategory));
      const forgeMaterials = matchingMaterials.filter(m => m.materialCategory === 'Forge');

      // Upload documents to S3 using Pipe category (use Pipe tags and content type)
      const pipePayload = {
        pipeId: Number(selectedPipeSize.pipeId),
        projectId: Number(selectedPipeSize.projectId),
        projectTypeId: Number(selectedPipeSize.projectTypeId),
        materialCategory: 'Pipe'
      };

      const s3Response = await getPreSignedUrlToUpload(materialDocuments, pipePayload, 1);

      if (s3Response && s3Response.length > 0) {
        const allMaterialUpdates: any[] = [];

        // Update Pipe materials
        if (pipeMaterials.length > 0) {
          const pipeUpdates = pipeMaterials.map((material: MaterialData) => {
            return {
              materialId: material.materialId,
              materialCategory: material.materialCategory,
              materialStatus: material.materialStatus || "Available",
              pipeId: material.pipeId,
              assemblyNumbers: material.assemblyNumbers || [],
              documents: s3Response.map((file: any, index: number) => ({
                fileName: file.fileName,
                s3Key: file.s3Key,
                version: 1,
                tags: selectedTags[index] || [],
                uploadedBy: user.email || ''
              })),
              details: material.details || {}
            };
          });
          allMaterialUpdates.push(...pipeUpdates);
        }

        // Update Forge materials with same S3 response
        if (forgeMaterials.length > 0) {
          const forgeUpdates = forgeMaterials.map((material: MaterialData) => {
            return {
              materialId: material.materialId,
              materialCategory: material.materialCategory,
              materialStatus: material.materialStatus || "Available",
              pipeId: material.pipeId,
              assemblyNumbers: material.assemblyNumbers || [],
              documents: s3Response.map((file: any, index: number) => ({
                fileName: file.fileName,
                s3Key: file.s3Key,
                version: 1,
                tags: selectedTags[index] || [],
                uploadedBy: user.email || ''
              })),
              details: material.details || {}
            };
          });
          allMaterialUpdates.push(...forgeUpdates);
        }

        // Update Pipe materials
        if (pipeMaterials.length > 0) {
          const pipeApiPayload = {
            data: allMaterialUpdates.filter(m => isPipeTabCategory(m.materialCategory))
          };
          await updateMaterialTraceability(pipeApiPayload, 'Pipe', false, true);
        }

        // Update Forge materials
        if (forgeMaterials.length > 0) {
          const forgeApiPayload = {
            data: allMaterialUpdates.filter(m => m.materialCategory === 'Forge')
          };
          await updateMaterialTraceability(forgeApiPayload, 'Forge', false, true);
        }
      }

      // customNotification.success(
      //   'Documents uploaded successfully',
      //   `Documents have been uploaded for ${matchingMaterials.length} material(s).`
      // );
      onUploadComplete();
      handleClose();
    } catch (error) {
      customNotification.error(
        'Upload failed',
        'There was an error uploading the documents. Please try again.'
      );
    } finally {
      setIsUploading(false);
    }
  };

  const handleClose = () => {
    setSelectedHeatNumbers([]);
    setMaterialDocuments([]);
    setSelectedTags([]);
    setIsUploading(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="w-[90vw] max-w-[1200px]">
        <DialogHeader>
          <DialogTitle className="flex flex-col gap-2">
            <div className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload Material Batch Certificate
            </div>
            {/* {selectedPipeSize && (
              <div className="text-sm font-normal text-gray-600 ml-7">
                Pipe Size: {selectedPipeSize.displayName || `${selectedPipeSize.pipeName || 'N/A'} (${selectedPipeSize.size || 'N/A'} x ${selectedPipeSize.wallThickness || 'N/A'})`}
              </div>
            )} */}
          </DialogTitle>
        </DialogHeader>

        <div className="grid p-2 grid-cols-4 gap-4 min-w-0 max-h-[75vh] flex flex-col overflow-y-auto overflow-x-hidden">
          {/* Pipe Size Display */}
          {selectedPipeSize && (
            <div className="space-y-2 col-span-4">
              <Label>Pipe Size:</Label>
              <div className="text-sm font-medium text-gray-700 p-2 border rounded bg-gray-50">
                {selectedPipeSize.displayName || `${selectedPipeSize.pipeName || 'N/A'} (${selectedPipeSize.size || 'N/A'} x ${selectedPipeSize.wallThickness || 'N/A'})`}
              </div>
            </div>
          )}

          {/* Heat Number Multi-Select */}
          <div className="space-y-2 col-span-4">
            <Label htmlFor="heatNumbers">Heat Number</Label>
            {availableHeatNumbers.length > 0 ? (
              <MultiSelect
                options={heatNumberOptions}
                selected={selectedHeatNumbers}
                onChange={setSelectedHeatNumbers}
                placeholder="Select heat numbers..."
                className="w-full"
              />
            ) : (
              <div className="text-sm text-gray-500 p-2 border rounded">
                No heat numbers available
              </div>
            )}
          </div>

          {/* Document Upload Section */}
          <div className="space-y-2 col-span-4">
            <Label>Upload Documents</Label>
            <p className="text-sm text-gray-600">
              Upload the material batch certificate.
              <br />
              <span className="text-xs text-gray-500">
                Allowed: PDF, Word (.doc, .docx), Excel (.xls, .xlsx), Text (.txt), Images (.jpg, .png, .gif)
                <br />
                Maximum file size: 100MB per file
              </span>
            </p>

            <FileUpload
              onFileSelect={handleMaterialFileSelect}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.jpg,.jpeg,.png,.gif"
              multiple={true}
              maxFiles={100}
              buttonText="Upload Material Batch Certificate"
              variant="outline"
              size="lg"
              onRemoveFile={handleRemoveFile}
              selectedFiles={materialDocuments}
              showFileList={materialDocuments.length > 0}
              className="w-full"
              showTags={true}
              optionsTags={tagOptions}
              selectedTags={selectedTags}
              showAddButton={true}
              addButtonText="Add New Tag"
              onAddButtonClick={() => setIsAddingTag(true)}
              onTagsChange={handleTagsChange}
              maxHeight=""
            />

            <div className="text-xs text-gray-500">
              Current files: {materialDocuments.length} file(s)
            </div>
          </div>
        </div>

        <DialogFooter className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex items-center gap-2"
            disabled={isUploading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpload}
            disabled={selectedHeatNumbers.length === 0 || materialDocuments.length === 0 || isUploading}
            loading={isUploading}
            loadingText="Uploading..."
            className="flex items-center gap-2"
          >
            <Upload className="h-4 w-4" />
            Upload
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Add New Tag Dialog */}
      <AddNewItemDialog
        open={isAddingTag}
        onOpenChange={setIsAddingTag}
        title="Add New Tag"
        label="Tag Name"
        placeholder="Enter tag name"
        onAdd={handleAddTag}
        buttonText="Add Tag"
        cancelText="Cancel"
      />
    </Dialog>
  );
};

